// SMO Lead Reports page and its dialogs. Excel files and values written to
// records stay Turkish; only on-screen texts live here.
const smoLead = {
  title: 'SMO Lead Reports',
  breadcrumb: 'Reports / SMO Lead Reports',

  header: {
    manualEntry: 'Manual Data Entry',
    refresh: 'Refresh',
    refreshing: 'Refreshing...',
    refreshHint: 'Refresh the workers',
    download: 'Download Table',
    downloadHint: 'Download the filtered worker table as Excel',
  },

  stats: {
    totalSmo: 'Total SMOs',
    centers: 'Sites of this SMO',
    workers: 'Workers under this SMO',
    centerSplit: '({{primer}} Primary, {{backup}} Backup)',
  },

  picker: {
    title: 'SMO List',
    subtitle: 'Select an SMO to see the details',
    loading: 'Loading the SMO list...',
    empty: 'No SMO has been assigned to any site yet.',
    backup: 'Backup',
    centerBackup: 'Site Backup',
    project: 'Project',
    centers_one: '{{count}} site',
    centers_other: '{{count}} sites',
    projectBackupHint: 'Backup view: {{project}}',
    centerBackupHint_one: 'Site backup view: {{count}} site',
    centerBackupHint_other: 'Site backup view: {{count}} sites',
    delegatedLegend: '“Backup”: you are the backup SMO Lead on a project, so you only see that project. “Site Backup”: limited to the sites where you are the backup SMO.',
    toggleHint: 'Click the selected SMO again to clear the selection.',
  },

  team: {
    title: '{{name}} - Workers',
    subtitle: 'Workers assigned at this SMO’s sites and their projects.',
    projectBackup: 'Backup view · {{project}}',
    projectBackupHint: 'Because you are a backup SMO Lead, this view is limited to the selected project',
    centerBackup_one: 'Site backup view · {{count}} site',
    centerBackup_other: 'Site backup view · {{count}} sites',
    centerBackupHint: 'This view is limited to the sites where you are the backup SMO',
    removeSmo: 'Remove SMO',
    removeSmoHint: 'Remove this SMO assignment from its sites',
    loading: 'Loading workers...',
    noMatch: 'No worker matches your search.',
    empty: 'There are no workers under this SMO.',
    missingDocs_one: '{{count}} worker is missing CV/GCP/IATA documents. Tap a worker’s folder icon to see what is missing.',
    missingDocs_other: '{{count}} workers are missing CV/GCP/IATA documents. Tap a worker’s folder icon to see what is missing.',
  },

  centers: {
    unknownCity: 'Unknown city',
    unknownDistrict: 'Unknown district',
    cityCount_one: '{{count}} site',
    cityCount_other: '{{count}} sites',
    cityCountDistricts_one: '{{count}} site · {{districts}} districts',
    cityCountDistricts_other: '{{count}} sites · {{districts}} districts',
    districtCount_one: '{{count}} site',
    districtCount_other: '{{count}} sites',
    primerHint: 'This site’s primary SMO',
    backupHint: 'This site’s backup SMO',
    legendPrimer: 'Primary SMO',
    legendBackup: 'Backup SMO',
    legendToggle: 'Tap a city to see its sites; cities with more than 10 sites are split by district.',
  },

  filters: {
    search: 'Search worker, project or site...',
    status: 'Status',
    type: 'Type',
    blinded: 'Blinding',
    finished: 'Finished Projects',
    allCount: 'All ({{count}})',
    allTypes: 'All Types ({{count}})',
    active: 'Active',
    inactive: 'Inactive',
    incomplete: 'Incomplete',
    hideFinished: 'Hide finished projects',
    finishedHidden: 'Finished projects hidden',
    finishedHint: 'Hide projects marked as finished',
    remove: 'Remove filter',
    clearAll: 'Clear All',
    help: 'Filters apply to the workers’ project assignments; a worker with no matching assignment drops out of the list. “Incomplete” lists only workers with unfinished visits or tasks and shows them when the row is opened.',
  },

  table: {
    expand: 'Open / close',
    worker: 'Worker',
    email: 'E-mail',
    phone: 'Phone',
    projectCount: 'Projects',
    centers: 'Site(s)',
    documents: 'Documents',
    action: 'Action',
    removeWorker: 'Remove this worker from this SMO',
    viewDocs: '{{name}} - view CV/GCP/IATA documents',
    missingDocs: 'Missing document: {{list}}',
    incompleteVisits_one: '{{count}} incomplete visit',
    incompleteVisits_other: '{{count}} incomplete visits',
    rowHint: 'Click a row to open the worker’s projects.',
    docsLegend: 'CV/GCP/IATA documents (red number: missing required documents)',
    incompleteLegend: 'Incomplete visits',
    scrollLeft: 'Scroll the table left',
    scrollRight: 'Scroll the table right',
    showProjects: 'Show projects',
    hideProjects: 'Hide projects',
  },

  projects: {
    finished: 'Finished',
    role: 'Role',
    center: 'Site',
    city: 'City',
    type: 'Type',
    blinded: 'Blinding',
    rate: 'Rate',
    assignedAt: 'Assigned On',
    viewVisits: 'View Visits',
    incompleteHint_one: '{{count}} visit not completed',
    incompleteHint_other: '{{count}} visits not completed',
    help: '“View Visits” opens the worker’s visits in that project; an orange button and number mean there are incomplete visits.',
  },

  tasks: {
    title: 'Incomplete Tasks ({{count}})',
    dialogTitle: 'Incomplete Tasks',
    visit: 'Visit',
    job: 'Task',
    calendarJob: 'Calendar Task',
    patient: 'Patient',
    homeVisit: 'Home Visit',
    siteVisit: 'Site Visit',
    notDone: 'Not completed',
    allDone: 'Great! Every task is completed.',
    summary: '{{total}} incomplete tasks found ({{visits}} visits, {{jobs}} calendar tasks).',
    columns: {
      date: 'Date',
      worker: 'Worker',
      type: 'Task Type',
      description: 'Description',
      status: 'Status',
    },
  },

  section: {
    show: 'Show',
    hide: 'Hide',
    expand: 'Expand',
    collapse: 'Collapse',
    excelHint: 'Download as Excel',
    loading: 'Loading...',
    noVisits: 'No visits in the selected date range.',
  },

  dateRange: {
    label: 'Date Range:',
    start: 'Start date',
    end: 'End date',
    thisMonth: 'This Month',
    thisMonthHint: 'From the 1st of this month to today',
    all: 'All',
    allHint: 'All time',
    allRecorded: 'Whole recorded period',
    allActive: 'No date limit',
  },

  sections: {
    overview: {
      title: 'Operational Overview',
      text: 'Summary of every visit in the selected period: quality, SLA, documentation and distance travelled.',
      status: '{{total}} visits analysed ({{home}} home / {{site}} site)',
    },
    quality: {
      title: 'Quality & Clinical Risk Panel',
      text: 'Quality and clinical risk indicators such as protocol deviations, CAPA, screen fail, drop patient, AE/SAE and DCF.',
      status_one: '{{count}} visit',
      status_other: '{{count}} visits',
    },
    performance: {
      title: 'Team Performance Analysis',
      text: 'Visits, SLA, eCRF / source document completeness, distance and delays per site coordinator.',
      status_one: '{{count}} worker analysed',
      status_other: '{{count}} workers analysed',
    },
    activity: {
      title: 'Team System Usage',
      text: 'Who is online right now, plus the time the team spends in the system and their logins.',
      status: '{{online}} / {{total}} online',
    },
    training: {
      title: 'Training and SOP Tracking',
      text: 'Team members who have not yet completed the SOPs and trainings assigned to them.',
      pending_one: '{{count}} worker not completed',
      pending_other: '{{count}} workers not completed',
      overdue_one: '{{count}} overdue',
      overdue_other: '{{count}} overdue',
      allDone: 'The whole team is done',
    },
  },

  common: {
    detailHint: 'Click to see the details',
    days: '{{value}} days',
  },

  overview: {
    clickHint: 'Click the boxes with a magnifier and the alerts to open the visits behind them (who, when, what happened).',
    totalVisits: 'Total Visits',
    homeSite: '{{home}} home · {{site}} site',
    qualityRate: 'Issue-free Visit Rate',
    issueVisits_one: '{{count}} visit with issues',
    issueVisits_other: '{{count}} visits with issues',
    issueHint: 'See the visits with issues',
    slaRate: 'SLA Met',
    overdueVisits_one: '{{count}} overdue visit',
    overdueVisits_other: '{{count}} overdue visits',
    overdueHint: 'See the overdue visits',
    docRate: 'Documentation Completeness',
    docSub: 'Average of eCRF + Source + Signature',
    totalKm: 'Total KM',
    kmSub: 'Distance travelled',
    comparison: 'Home vs Site Comparison',
    cycle: 'Cycle Times & Process Summary',
    cycleHome: 'Avg. Cycle — Home',
    cycleSite: 'Avg. Cycle — Site',
    missingProcessEnd: 'Process End Date Missing',
    missed: 'Missed Visits',
    cycleHelp: 'Cycle time: days from the visit date to the process end date. Visits without a process end date are left out of the average.',
    monthlyTrend: 'Monthly Visit Trend',
    qualityPie: 'Visit Quality Distribution',
    coordinatorTable: 'Home Visits per Coordinator',
  },

  alerts: {
    overdue: 'SLA met rate {{rate}}% — below target',
    issues: 'Issue rate {{rate}}% — high',
    deviation: 'Protocol deviation rate {{rate}}%',
    missingProcessEnd_one: '{{count}} visit missing a process end date',
    missingProcessEnd_other: '{{count}} visits missing a process end date',
    missed_one: '{{count}} visit missed',
    missed_other: '{{count}} visits missed',
    aesae_one: '{{count}} AE/SAE reported',
    aesae_other: '{{count}} AE/SAEs reported',
  },

  issue: {
    issues: 'Visits with Issues',
    overdue: 'SLA Overdue Visits',
    missingProcessEnd: 'Visits Missing a Process End Date',
    missed: 'Missed Visits',
    personIssues: '{{name}} — Issues',
    personMissed: '{{name}} — Missed',
    personIssueDeviation: '{{name}} — Issues & Deviations',
    personOverdue: '{{name}} — Overdue Visits',
  },

  coordinators: {
    coordinator: 'Coordinator',
    homeVisits: 'Home Visits',
    issues: 'Issues',
    missed: 'Missed',
    aesae: 'AE/SAE',
    dcf: 'DCF',
    issueRate: 'Issue Rate',
    total: 'TOTAL',
  },

  metrics: {
    deviation: 'Protocol Deviation',
    capa: 'CAPA Opened',
    screenFail: 'Screen Fail',
    dropPatient: 'Drop Patient',
    aesae: 'AE/SAE',
    dcf: 'DCF',
  },

  quality: {
    clickHint: 'Click boxes and rows above zero to see the visits behind them.',
    inVisits_one: '{{name}} — in {{count}} visit',
    inVisits_other: '{{name}} — in {{count}} visits',
    safetyTitle: 'Patient Safety & Compliance',
    siteTitle: 'Site Visits — Patient Status',
    aeSaeReported: 'AE/SAE Reported',
    dcfCreated: 'DCF Created',
    missedVisit: 'Missed Visits',
    totalSite: 'Total Site Visits',
    screenFail: 'Screen Fail',
    dropPatient: 'Drop Patient',
    slaOverdue: 'SLA Overdue',
    missingProcessEnd: 'Process End Date Missing',
    coordinatorChart: 'Issues per Coordinator',
    monthlyIssues: 'Monthly Issue Trend',
    chartsNote: 'These two charts are built from home visits and use the Operational Overview’s date range.',
  },

  performance: {
    help: 'Click the underlined numbers to open visits with issues or delays. “Statistics” opens the worker’s detailed statistics page with this date range.',
    stats: 'Statistics',
    statsHint: 'Detailed statistics',
    workload: 'Workload (Visit Count)',
    kmPerEmployee: 'KM per Worker',
    slaHomeSite: 'Home vs Site: SLA Met',
    columns: {
      coordinator: 'Site Coordinator',
      visits: 'Visits (Home/Site)',
      sla: 'SLA %',
      ecrf: 'eCRF %',
      source: 'Source %',
      issueDeviation: 'Issues / Deviations',
      km: 'KM',
      overdue: 'Delays',
      actions: 'Actions',
    },
  },

  chart: {
    home: 'Home',
    visits: 'Visits',
    site: 'Site',
    issues: 'Issues',
    missed: 'Missed',
    homeVisit: 'Home Visit',
    siteVisit: 'Site Visit',
    slaMet: 'SLA Met %',
    issueRate: 'Issue Rate (%)',
    slaCompliance: 'SLA Compliance (%)',
    documentation: 'Documentation (%)',
    withIssue: 'Visits with Issues',
    withoutIssue: 'Issue-free Visits',
  },

  time: {
    hm: '{{h}} h {{m}} min',
    m: '{{m}} min',
    s: '{{s}} s',
    noActivity: 'No activity',
    justNow: 'Just now',
    minutesAgo_one: '{{count}} min ago',
    minutesAgo_other: '{{count}} min ago',
    hoursAgo_one: '{{count}} hour ago',
    hoursAgo_other: '{{count}} hours ago',
    daysAgo_one: '{{count}} day ago',
    daysAgo_other: '{{count}} days ago',
  },

  deadline: {
    none: 'No due date',
    overdue_one: '{{count}} day overdue',
    overdue_other: '{{count}} days overdue',
    today: 'Due today',
    lastDay: '1 day left',
    left: '{{count}} days left',
  },

  activity: {
    loading: 'Loading usage data...',
    noWorkers: 'No workers found under this SMO.',
    error: 'Could not load the usage history.',
    online: 'Online',
    offline: 'Offline',
    window: 'Usage history is kept for the last {{days}} days (since {{date}}).',
    tiles: {
      online: 'Online Now',
      total: 'Team Total Time',
      avg: 'Average per Person',
      logins: 'Total Logins',
      active: 'Active Users in Range',
    },
    columns: {
      worker: 'Worker',
      status: 'Status',
      range: 'Time in Range',
      activeDays: 'Active Days',
      dailyAvg: 'Daily Avg.',
      logins: 'Logins',
      lastActivity: 'Last Activity',
      total: 'Total',
    },
    detail: {
      sessions: 'Sessions (in range)',
      totalSessions: 'Total Sessions (all time)',
      avgSession: 'Avg. Session Length',
      lastActivity: 'Last Activity',
      daily: 'Daily Breakdown',
      noDays: 'No recorded usage in the selected date range.',
    },
    dailyChart: 'Team Daily Total (hours)',
    perEmployeeChart: 'Total Time per Worker (hours)',
    noRecords: 'No records in the selected date range.',
    hourUnit: ' h',
    hours: '{{value}} hours',
    dailyTooltip: '{{hours}} hours · {{people}} people',
    totalLabel: 'Total',
    durationLabel: 'Time',
    footNote: 'Times are counted while the user is signed in and the tab is open, and are stored per day. The last few minutes of an open session may not be in the daily total yet. Tracking was enabled recently, so there are no records before that date.',
  },

  training: {
    loading: 'Loading training and SOP status...',
    error: 'Could not load the training and SOP status.',
    allDone: 'Everyone on the team has completed the SOPs and trainings assigned to them.',
    education: 'Training',
    note: 'Assignments go by role; records completed on the worker’s profile drop off here.',
    noDeadline: 'No due date',
    overdueBadge_one: '{{count}} overdue',
    overdueBadge_other: '{{count}} overdue',
    tiles: {
      pendingPeople: 'Workers Not Done',
      pendingSops: 'Pending SOPs',
      pendingEducations: 'Pending Trainings',
      overdue: 'Past Due Date',
    },
    columns: {
      worker: 'Worker',
      role: 'Role',
      pendingSop: 'Pending SOPs',
      pendingEducation: 'Pending Trainings',
      nextDeadline: 'Nearest Due Date',
    },
  },

  documents: {
    title: 'CV / GCP / IATA Documents',
    loading: 'Loading documents...',
    error: 'Could not load the documents.',
    empty: 'This worker has no CV/GCP/IATA documents.',
    missing: 'Missing required document: {{list}}',
    view: 'View',
    download: 'Download',
    help: 'These are the same documents as the CV/GCP/IATA section of Employee Management; here they can only be viewed and downloaded.',
  },

  issueModal: {
    count_one: '{{count}} record',
    count_other: '{{count}} records',
    search: 'Search worker, project, category or description...',
    empty: 'No records to show.',
    unknownWorker: 'Unknown worker',
    noDate: 'No date',
    patient: 'Patient: {{id}}',
    category: 'Source / Category',
    details: 'Issue Details',
    notes: 'Notes',
    categories: {
      iv_access: 'IV Access',
      patient_compliance: 'Patient Compliance',
      documentation: 'Documentation',
      logistics: 'Logistics',
      protocol: 'Protocol',
      equipment: 'Equipment',
      other: 'Other',
    },
    flags: {
      issueOccurred: 'Issue',
      issueResultedInMiss: 'Missed',
      protocolDeviation: 'Protocol Deviation',
      capaOpened: 'CAPA Opened',
      screenFail: 'Screen Fail',
      dropPatient: 'Drop Patient',
      wasAeSaeTaken: 'AE/SAE',
      dcfCreated: 'DCF',
      overdue: 'SLA Overdue',
    },
  },

  confirm: {
    workerTitle: 'Remove Worker',
    smoTitle: 'Remove SMO',
    workerMessage: 'Are you sure you want to remove {{worker}} from the SMO {{smo}}?',
    workerNote: 'This removes all of the worker’s project assignments at this SMO’s sites and cannot be undone.',
    smoMessage: 'Are you sure you want to remove the SMO assignment of {{smo}}?',
    smoMessage_one: 'Are you sure you want to remove the SMO assignment of {{smo}} from {{count}} site?',
    smoMessage_other: 'Are you sure you want to remove the SMO assignment of {{smo}} from {{count}} sites?',
    smoNote: 'Sites and worker assignments are kept; only this person’s SMO assignment is cleared. You can assign the SMO again later in Site Management.',
    workerAction: 'Remove',
    smoAction: 'Remove',
    processing: 'Processing...',
    cancel: 'Cancel',
  },

  toast: {
    smoListError: 'Could not load the SMO list!',
    workersUpdated: 'Workers updated!',
    workersError: 'Could not load the workers!',
    noVisits: 'This worker has no visits in this project.',
    excelDownloaded: 'Excel file downloaded',
    overviewExcel: 'Operational summary Excel downloaded',
    qualityExcel: 'Quality & risk Excel downloaded',
    performanceExcel: 'Worker performance Excel downloaded',
    workerRemoved: '{{name}} was removed from this SMO.',
    workerRemoveError: 'Could not remove the worker!',
    smoRemoved: 'The SMO assignment of {{name}} was removed.',
    smoRemoved_one: 'The SMO assignment of {{name}} was removed from {{count}} site.',
    smoRemoved_other: 'The SMO assignment of {{name}} was removed from {{count}} sites.',
    smoRemoveError: 'Could not remove the SMO!',
  },

  entry: {
    title: 'Manual Data Entry',
    subtitle: 'Visit Evaluation Table',
    help: 'Pick a worker: edit the existing visits in the cells and add new ones with “Add Row”. Changes are saved together with “Save All”. The magnifier in a column header opens the filter row.',
    siteHelp: 'Cycle time and the SLA columns are calculated from the visit date, process end date and SLA target.',
    tabs: {
      label: 'Visit type',
      home: 'Home Visits',
      site: 'Site Visits',
    },
    worker: 'Worker',
    chooseWorker: '-- Select a Worker --',
    project: 'Project',
    allProjects: '-- All Projects --',
    patient: 'Patient ID',
    allPatients: '-- All Patients --',
    chooseProject: '-- Select a Project --',
    count_one: '{{count}} record',
    count_other: '{{count}} records',
    countFiltered: '{{shown}} / {{total}} records',
    existingCount_one: '({{count}} existing)',
    existingCount_other: '({{count}} existing)',
    newCount_one: '({{count}} new)',
    newCount_other: '({{count}} new)',
    rows_one: '{{count}} row',
    rows_other: '{{count}} rows',
    rowsFiltered: '{{shown}} / {{total}} rows',
    existingBadge: 'Existing: {{count}}',
    newBadge: 'New: {{count}}',
    clearFilters: 'Clear Filters',
    clearFiltersHint: 'Clear every filter',
    addRow: 'Add Row',
    loading: 'Loading visits...',
    pickWorker: 'Select a worker to enter data',
    noHomeVisits: 'This worker has no visits. Add a row to enter data.',
    noSiteVisits: 'This worker has no site visits. Add a row to enter data.',
    cancel: 'Cancel',
    saveAll: 'Save All',
    saving: 'Saving...',
    delete: 'Delete',
    removeRow: 'Remove row',
    new: 'New',
    existing: 'Existing',
    search: 'Search...',
    filterHint: 'Show / hide column filters',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    hours: 'Hours',
    minutes: 'Minutes',
    markNa: 'Mark as N/A',
    groups: {
      identification: 'Identification',
      window: 'Window / Timing',
      scheduled: 'Scheduled',
      travelBefore: 'Travel (Before)',
      visit: 'Visit Details',
      travelAfter: 'Travel (After)',
      issue: 'Issue Information',
      correction: 'Quality Review – Correction Requests',
      source: 'Source Document Dates',
      outcome: 'Outcome',
      sourceDocument: 'Source Document',
      patientStatus: 'Patient Status',
      siteVisit: 'Visit',
      protocol: 'Protocol & CAPA',
      completion: 'Completion Dates',
      sla: 'Process & SLA',
      issueShort: 'Issue',
    },
    home: {
      project: 'Project',
      patientId: 'Patient ID',
      visitNumber: 'Visit ID',
      windowOpen: 'Window Opens',
      targetDate: 'Target Date',
      windowClose: 'Window Closes',
      clinician: 'Clinician',
      scheduledDate: 'Scheduled Date',
      scheduledTime: 'Scheduled Time',
      startDate: 'Start Date',
      startTime: 'Start Time',
      endDate: 'End Date',
      endTime: 'End Time',
      durationHours: 'Duration (h)',
      durationMinutes: 'Duration (min)',
      issueOccurred: 'Issue Occurred?',
      issueResult: 'Issue Result?',
      issueSource: 'Issue Source',
      countryCorrections: 'Country Coordinator Correction Requests',
      pmCorrections: 'Clinical Project Manager Correction Requests',
      correctionReason: 'Correction Reason / Comment',
      initialSource: 'Initial Source Date',
      latestRevision: 'Latest Revision Date',
      sourceFinalized: 'Source Finalized',
      aeSae: 'AE/SAE Occurred?',
      dcf: 'DCF Created?',
    },
    site: {
      project: 'Project Name',
      center: 'Site',
      visitType: 'Visit Type',
      sourceDocument: 'Source Document',
      sourceDocumentTime: 'Source Document Time',
      visitDate: 'Visit Date',
      protocolDeviation: 'Protocol Deviation?',
      capaOpened: 'CAPA Opened?',
      comments: 'Comments',
      ecrfDate: 'eCRF Completion',
      ecrfTime: 'eCRF Completion Time',
      sourceDocDate: 'Source Doc.',
      sourceDocTime: 'Source Doc. Time',
      signatureDate: 'Signature Completion',
      processEnd: 'Process End',
      cycleTime: 'Cycle Time (days)',
      slaTarget: 'SLA Target (days)',
      slaMet: 'SLA Met?',
      slaOverdue: 'SLA Exceeded?',
    },
    toast: {
      loadError: 'Could not load the visits',
      nothingToSave: 'There is nothing to save',
      pickWorker: 'Select a worker first',
      saved_one: '{{count}} record saved',
      saved_other: '{{count}} records saved',
      saveFailed_one: '{{count}} record could not be saved',
      saveFailed_other: '{{count}} records could not be saved',
      saveError: 'An error occurred while saving',
    },
  },
};

export default smoLead;
